import { useState, useEffect, useRef, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { getColumnKey, resolveActualKey, updateAllKeyVariants } from "@/components/tableau/utils/formatters";

export function useCellEditing({ orders, setOrders, columns, markRowAsModified, onStatusTerminated }) {
  const [selectedCell, setSelectedCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerPosition, setDatePickerPosition] = useState({ top: 0, left: 0 });
  const [datePickerContext, setDatePickerContext] = useState({ orderId: null, columnName: null, currentValue: null });

  const [showDropdownPicker, setShowDropdownPicker] = useState(false);
  const [dropdownPickerPosition, setDropdownPickerPosition] = useState({ top: 0, left: 0 });
  const [dropdownPickerContext, setDropdownPickerContext] = useState({ orderId: null, columnName: null, currentValue: null });
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [customDropdownValue, setCustomDropdownValue] = useState("");

  const [inlineEdit, setInlineEdit] = useState({ orderId: null, columnName: null, value: "", type: null });
  const inlineInputRef = useRef(null);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  // Close date picker on outside click
  useEffect(() => {
    const handler = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-dropdown')) {
        setShowDatePicker(false);
        setDatePickerContext({ orderId: null, columnName: null, currentValue: null });
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDatePicker]);

  // Close dropdown picker on outside click
  useEffect(() => {
    const handler = (event) => {
      if (showDropdownPicker && !event.target.closest('.dropdown-picker-popup')) {
        setShowDropdownPicker(false);
        setDropdownPickerContext({ orderId: null, columnName: null, currentValue: null });
        setCustomDropdownValue("");
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdownPicker]);

  // Auto-focus inline input
  useEffect(() => {
    if (inlineEdit.orderId && inlineInputRef.current) {
      inlineInputRef.current.focus();
      inlineInputRef.current.select();
    }
  }, [inlineEdit.orderId]);

  const handleCheckboxToggle = useCallback(async (orderId, columnName, currentValue) => {
    const order = ordersRef.current.find(o => o.order_id === orderId);
    if (!order) return;
    const key = resolveActualKey(order.extracted_data || {}, columnName);
    const currentBool = currentValue === true || currentValue === 'true' || currentValue === '1' || currentValue === 'oui';
    const updatedData = { ...order.extracted_data, [key]: !currentBool };
    try {
      const response = await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(orderId);
      }
    } catch { toast.error("Erreur lors de la mise à jour"); }
  }, [setOrders, markRowAsModified]);

  // Fire the "order terminated" callback when ORDER STATUS is set to TERMINE, regardless of
  // whether the column is edited as free text (popup), inline, or via a dropdown.
  const checkTerminatedTrigger = useCallback((columnName, value, order, updatedData) => {
    const colNorm = (columnName || '').trim().toUpperCase();
    const valNorm = String(value ?? '').trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (colNorm === 'ORDER STATUS' && valNorm === 'TERMINE' && onStatusTerminated) {
      onStatusTerminated({ ...order, extracted_data: updatedData });
    }
  }, [onStatusTerminated]);

  const handleCellClick = useCallback((columnName, content, rowIdx, orderId, columnType, event) => {
    if (columnType === 'checkbox') { handleCheckboxToggle(orderId, columnName, content); return; }
    if (columnType === 'date') {
      const rect = event?.currentTarget?.getBoundingClientRect();
      if (rect) setDatePickerPosition({ top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 320), cellHeight: rect.height });
      setDatePickerContext({ orderId, columnName, currentValue: content });
      setShowDatePicker(true);
      return;
    }
    if (columnType === 'dropdown') {
      const rect = event?.currentTarget?.getBoundingClientRect();
      if (rect) setDropdownPickerPosition({ top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 280), cellHeight: rect.height });
      const key = getColumnKey(columnName);
      const uniqueValues = new Set();
      ordersRef.current.forEach(order => {
        const val = order.extracted_data?.[key];
        if (val !== undefined && val !== null && String(val).trim() !== '') uniqueValues.add(String(val).trim());
      });
      setDropdownOptions(Array.from(uniqueValues).sort());
      setDropdownPickerContext({ orderId, columnName, currentValue: content });
      setCustomDropdownValue("");
      setShowDropdownPicker(true);
      return;
    }
    if (['amount', 'numerique', 'phone', 'calcul'].includes(columnType)) {
      const rawValue = content ? (columnType === 'phone'
        ? String(content).replace(/[^\d+]/g, '')
        : String(content).replace(/[^\d.,-]/g, '').replace(',', '.')) : '';
      setInlineEdit({ orderId, columnName, value: rawValue, type: columnType });
      return;
    }
    setSelectedCell({ columnName, content, rowIdx, orderId });
    setEditValue(content);
    setIsEditing(true);
  }, [handleCheckboxToggle]);

  const handleInlineAmountSave = useCallback(async () => {
    if (!inlineEdit.orderId) return;
    const order = ordersRef.current.find(o => o.order_id === inlineEdit.orderId);
    if (!order) { setInlineEdit({ orderId: null, columnName: null, value: "", type: null }); return; }
    let newValue;
    if (inlineEdit.type === 'phone') {
      newValue = inlineEdit.value.trim();
    } else {
      const cleanValue = inlineEdit.value.replace(/\s/g, '').replace(',', '.');
      const numValue = parseFloat(cleanValue);
      if (inlineEdit.value.trim() !== '' && isNaN(numValue)) { toast.error("Valeur invalide : veuillez entrer un nombre valide"); return; }
      newValue = inlineEdit.value.trim() === '' ? "" : numValue;
    }
    let updatedData;
    if (inlineEdit.type === 'calcul') {
      const overrideKey = `_override_${inlineEdit.columnName}`;
      if (newValue === "" || newValue === null) { updatedData = { ...order.extracted_data }; delete updatedData[overrideKey]; }
      else { updatedData = { ...order.extracted_data, [overrideKey]: newValue }; }
    } else {
      const key = resolveActualKey(order.extracted_data || {}, inlineEdit.columnName);
      updatedData = { ...order.extracted_data, [key]: newValue };
    }
    try {
      const response = await authFetch(`${API}/orders/${inlineEdit.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: inlineEdit.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === inlineEdit.orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(inlineEdit.orderId);
      }
    } catch { toast.error("Erreur lors de la mise à jour"); }
    setInlineEdit({ orderId: null, columnName: null, value: "", type: null });
  }, [inlineEdit, setOrders, markRowAsModified]);

  const handleInlineAmountCancel = useCallback(() => {
    setInlineEdit({ orderId: null, columnName: null, value: "", type: null });
  }, []);

  const handleRestoreCalculatedValue = useCallback(async (orderId, columnName, event) => {
    if (event) event.stopPropagation();
    const order = ordersRef.current.find(o => o.order_id === orderId);
    if (!order) return;
    const overrideKey = `_override_${columnName}`;
    if (order.extracted_data?.[overrideKey] === undefined) return;
    const updatedData = { ...order.extracted_data };
    delete updatedData[overrideKey];
    try {
      const response = await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(orderId);
        toast.success("Valeur calculée restaurée");
      }
    } catch { toast.error("Erreur lors de la restauration"); }
  }, [setOrders, markRowAsModified]);

  const handleDateSelect = useCallback(async (date) => {
    if (!datePickerContext.orderId || !date) return;
    const order = ordersRef.current.find(o => o.order_id === datePickerContext.orderId);
    if (!order) return;
    const key = resolveActualKey(order.extracted_data || {}, datePickerContext.columnName);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const updatedData = { ...order.extracted_data, [key]: `${year}-${month}-${day}` };
    try {
      const response = await authFetch(`${API}/orders/${datePickerContext.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: datePickerContext.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === datePickerContext.orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(datePickerContext.orderId);
        setShowDatePicker(false);
        setDatePickerContext({ orderId: null, columnName: null, currentValue: null });
      }
    } catch { toast.error("Erreur lors de la mise à jour de la date"); }
  }, [datePickerContext, setOrders, markRowAsModified]);

  const handleClearDate = useCallback(async (orderId, columnName) => {
    const order = ordersRef.current.find(o => o.order_id === orderId);
    if (!order) return;
    const key = resolveActualKey(order.extracted_data || {}, columnName);
    const updatedData = { ...order.extracted_data };
    delete updatedData[key];
    try {
      const response = await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(orderId);
        toast.success("Date effacée");
      }
    } catch { toast.error("Erreur lors de la suppression de la date"); }
  }, [setOrders, markRowAsModified]);

  const parseDateValue = useCallback((value) => {
    if (!value) return undefined;
    const str = String(value);
    const frMatch = str.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
    if (frMatch) return new Date(parseInt(frMatch[3]), parseInt(frMatch[2]) - 1, parseInt(frMatch[1]));
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    const parsed = new Date(str);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }, []);

  const handleDropdownSelect = useCallback(async (value) => {
    if (!dropdownPickerContext.orderId) return;
    const order = ordersRef.current.find(o => o.order_id === dropdownPickerContext.orderId);
    if (!order) return;
    const updatedData = updateAllKeyVariants(order.extracted_data || {}, dropdownPickerContext.columnName, value);
    try {
      const response = await authFetch(`${API}/orders/${dropdownPickerContext.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: dropdownPickerContext.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === dropdownPickerContext.orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(dropdownPickerContext.orderId);
        setShowDropdownPicker(false);
        setDropdownPickerContext({ orderId: null, columnName: null, currentValue: null });
        checkTerminatedTrigger(dropdownPickerContext.columnName, value, order, updatedData);
      }
    } catch { toast.error("Erreur lors de la mise à jour"); }
  }, [dropdownPickerContext, setOrders, markRowAsModified, checkTerminatedTrigger]);

  const handleAddCustomDropdownOption = useCallback(async () => {
    const trimmedValue = customDropdownValue.trim();
    if (!trimmedValue) return;
    if (!dropdownOptions.includes(trimmedValue)) setDropdownOptions(prev => [...prev, trimmedValue].sort());
    await handleDropdownSelect(trimmedValue);
    setCustomDropdownValue("");
  }, [customDropdownValue, dropdownOptions, handleDropdownSelect]);

  const handleDeleteDropdownOption = useCallback(async (columnName, optionToDelete) => {
    if (!columnName || !optionToDelete) return;
    const column = columns.find(c => c.name === columnName);
    if (!column) return;
    try {
      const response = await authFetch(`${API}/columns/${encodeURIComponent(columnName)}/options?option=${encodeURIComponent(optionToDelete)}`, { method: "DELETE" });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || "Erreur"); }
      const result = await response.json();
      setDropdownOptions(prev => prev.filter(o => o !== optionToDelete));
      const key = getColumnKey(columnName);
      setOrders(prev => prev.map(o => {
        const value = o.extracted_data?.[key] || o.extracted_data?.[columnName];
        if (value === optionToDelete) {
          const updatedData = { ...o.extracted_data };
          if (updatedData[key] === optionToDelete) updatedData[key] = "";
          if (updatedData[columnName] === optionToDelete) updatedData[columnName] = "";
          return { ...o, extracted_data: updatedData };
        }
        return o;
      }));
      toast.success(`Option "${optionToDelete}" supprimée (${result.orders_updated} cellule(s) vidée(s))`);
    } catch (error) { toast.error(error.message || "Erreur lors de la suppression de l'option"); }
  }, [columns, setOrders]);

  const handleClearDropdownValue = useCallback(async () => {
    if (!dropdownPickerContext.orderId) return;
    const order = ordersRef.current.find(o => o.order_id === dropdownPickerContext.orderId);
    if (!order) return;
    const key = resolveActualKey(order.extracted_data || {}, dropdownPickerContext.columnName);
    const updatedData = { ...order.extracted_data, [key]: "" };
    try {
      const response = await authFetch(`${API}/orders/${dropdownPickerContext.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: dropdownPickerContext.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === dropdownPickerContext.orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(dropdownPickerContext.orderId);
        setShowDropdownPicker(false);
        setDropdownPickerContext({ orderId: null, columnName: null, currentValue: null });
      }
    } catch { toast.error("Erreur lors de la suppression"); }
  }, [dropdownPickerContext, setOrders, markRowAsModified]);

  const handleSaveCell = useCallback(async () => {
    if (!selectedCell) return;
    const order = ordersRef.current.find(o => o.order_id === selectedCell.orderId);
    if (!order) return;
    const updatedData = updateAllKeyVariants(order.extracted_data || {}, selectedCell.columnName, editValue);
    try {
      const response = await authFetch(`${API}/orders/${selectedCell.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: selectedCell.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === selectedCell.orderId ? { ...o, extracted_data: updatedData } : o));
        setSelectedCell(prev => ({ ...prev, content: editValue }));
        setIsEditing(false);
        markRowAsModified(selectedCell.orderId);
        toast.success("Cellule mise à jour");
        checkTerminatedTrigger(selectedCell.columnName, editValue, order, updatedData);
      }
    } catch { toast.error("Erreur lors de la sauvegarde"); }
  }, [selectedCell, editValue, setOrders, markRowAsModified, checkTerminatedTrigger]);

  const handleSaveCellWithValue = useCallback(async (value) => {
    if (!selectedCell) return;
    const order = ordersRef.current.find(o => o.order_id === selectedCell.orderId);
    if (!order) return;
    const updatedData = updateAllKeyVariants(order.extracted_data || {}, selectedCell.columnName, value);
    try {
      const response = await authFetch(`${API}/orders/${selectedCell.orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: selectedCell.orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === selectedCell.orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(selectedCell.orderId);
        toast.success("Statut mis à jour");
        checkTerminatedTrigger(selectedCell.columnName, value, order, updatedData);
        setSelectedCell(null);
      }
    } catch { toast.error("Erreur lors de la sauvegarde"); }
  }, [selectedCell, setOrders, markRowAsModified, checkTerminatedTrigger]);

  const handleCopyContent = useCallback(async () => {
    const content = isEditing ? editValue : selectedCell?.content;
    if (content) {
      try { await navigator.clipboard.writeText(content); toast.success("Contenu copié !"); }
      catch { toast.error("Erreur lors de la copie"); }
    }
  }, [isEditing, editValue, selectedCell]);

  const handlePrintContent = useCallback(() => {
    if (!selectedCell) return;
    const content = isEditing ? editValue : selectedCell.content;
    const order = ordersRef.current.find(o => o.order_id === selectedCell.orderId);
    const extractedData = order?.extracted_data || {};
    const clientName = extractedData['NAME'] || extractedData['name'] || extractedData['NOM'] || '';
    const orderDate = extractedData['DATE OF ORDER'] || extractedData['DATE_OF_ORDER'] || '';
    const titleWithClient = clientName && selectedCell.columnName !== 'NAME' ? `${clientName} - ${selectedCell.columnName}` : selectedCell.columnName;
    const metaInfo = orderDate ? `Ligne ${selectedCell.rowIdx + 1} • Commande du ${orderDate} • Imprimé le ${new Date().toLocaleDateString('fr-FR')}` : `Ligne ${selectedCell.rowIdx + 1} • Imprimé le ${new Date().toLocaleDateString('fr-FR')}`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Impression - ${titleWithClient}</title><style>body{font-family:Arial,sans-serif;padding:40px}h1{font-size:18px;color:#333;border-bottom:2px solid #f97316;padding-bottom:10px}.meta{color:#666;font-size:12px;margin-bottom:20px}.content{white-space:pre-wrap;line-height:1.6;font-size:14px}</style></head><body><h1>${titleWithClient}</h1><div class="meta">${metaInfo}</div><div class="content">${selectedCell.content}</div></body></html>`);
    printWindow.document.close();
    printWindow.print();
  }, [selectedCell, isEditing, editValue]);

  return {
    selectedCell, setSelectedCell, editValue, setEditValue, isEditing, setIsEditing,
    showDatePicker, setShowDatePicker, datePickerPosition, datePickerContext, setDatePickerContext,
    showDropdownPicker, setShowDropdownPicker, dropdownPickerPosition, dropdownPickerContext, setDropdownPickerContext,
    dropdownOptions, setDropdownOptions, customDropdownValue, setCustomDropdownValue,
    inlineEdit, setInlineEdit, inlineInputRef,
    handleCellClick, handleInlineAmountSave, handleInlineAmountCancel,
    handleRestoreCalculatedValue, handleDateSelect, handleClearDate, parseDateValue,
    handleDropdownSelect, handleAddCustomDropdownOption,
    handleDeleteDropdownOption, handleClearDropdownValue,
    handleSaveCell, handleSaveCellWithValue, handleCopyContent, handlePrintContent,
  };
}
